import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, CheckCircle, FileText, Search, Scale, Gavel, MessageSquare, Shield, Clock, Users, TrendingUp, Mail, GitCompare, BookOpen, Bot, Laptop, Building, FileEdit, Mic } from 'lucide-react';
import MatrixBackground from '@/MatrixBackground';
import MarketingHeader from '@/components/MarketingHeader';
import usePageSEO from '@/hooks/usePageSEO';

const caseStudyData = {
  'contract-review-commercial-law': {
    category: 'Contract Intelligence',
    title: 'How a Commercial Law Firm Reviewed 200+ Contracts in One Week',
    metaDescription: 'Case study: A commercial law firm used Lawra AI to review 200+ contracts in one week, saving 78% of review time and catching 47 high-risk clauses during a portfolio acquisition.',
    icon: FileText,
    accentColor: '#D4AF37',
    heroStat: { metric: '78%', label: 'Time Saved on Contract Review' },
    overview: 'When a mid-size commercial law firm was tasked with reviewing over 200 contracts as part of a major portfolio acquisition, they faced a critical deadline. Manual review would have taken their team of six solicitors approximately four weeks. Using Lawra AI\'s Contract Review and Document Comparison tools, they completed the entire review in just one week.',
    challenge: {
      text: 'The firm was engaged to conduct a full contract review for a commercial property portfolio acquisition. The deal involved 200+ lease agreements, service contracts, and ancillary documents spanning multiple jurisdictions. Key challenges included:',
      points: [
        'Identifying non-standard clauses and potential liabilities across hundreds of documents',
        'Comparing lease terms against the client\'s standard requirements',
        'Flagging break clauses, rent review mechanisms, and unusual obligations',
        'Delivering a comprehensive risk report within a 10-day deadline',
      ],
    },
    solution: {
      text: 'The team used Lawra AI\'s suite of tools to systematically process the entire document set:',
      steps: [
        { tool: 'Contract Review', description: 'Each contract was uploaded and analysed for risk clauses, unusual terms, and potential liabilities. Lawra AI flagged 47 high-risk clauses that required immediate attention.' },
        { tool: 'Document Comparison', description: 'Lease agreements were compared against the client\'s standard terms, generating detailed deviation reports that highlighted exactly where each contract departed from expectations.' },
        { tool: 'Legal Summarisation', description: 'Complex 50+ page agreements were summarised into concise 2-page overviews, enabling partners to quickly grasp key terms without reading every document.' },
        { tool: 'Ask Lawra', description: 'Solicitors used the AI chatbot to ask specific questions about contract terms, getting instant answers referenced to specific clauses.' },
      ],
    },
    results: [
      { icon: Clock, metric: '78%', label: 'Time Reduction', description: 'Review completed in 7 days instead of the estimated 28 days' },
      { icon: TrendingUp, metric: '47', label: 'Critical Risks Found', description: 'High-risk clauses identified that manual review may have missed under time pressure' },
      { icon: Users, metric: '6', label: 'Solicitors Freed Up', description: 'Team members were able to focus on advisory work rather than document review' },
    ],
    quote: {
      text: 'Lawra AI didn\'t just save us time\u2014it gave us confidence. We caught risk clauses in the first pass that would normally only surface during detailed secondary review.',
      author: 'Senior Associate',
      firm: 'Commercial Law Firm, London',
    },
    relatedTool: '/tools/contract-review',
    relatedToolName: 'Try Contract Review',
  },

  'due-diligence-ma-transaction': {
    category: 'Due Diligence',
    title: 'Automating Due Diligence for a Multi-Million Pound M&A Deal',
    metaDescription: 'Case study: A corporate law team processed 340+ documents in 3 days using Lawra AI for M&A due diligence, uncovering 12 material issues including undisclosed litigation.',
    icon: Search,
    accentColor: '#00D4FF',
    heroStat: { metric: '340+', label: 'Documents Processed in 3 Days' },
    overview: 'A corporate law team handling a multi-million pound acquisition needed to process an extensive virtual data room. Lawra AI\'s Due Diligence tool enabled them to systematically analyse 340+ documents, extracting key findings and flagging material issues that informed the final deal terms.',
    challenge: {
      text: 'The acquirer\'s legal team had limited time to review a large and disorganised virtual data room before the exclusivity period expired. Challenges included:',
      points: [
        'Over 340 documents across corporate, employment, IP, and property categories',
        'Inconsistent document naming and organisation in the data room',
        'Tight timeline of 5 business days to deliver a due diligence report',
        'Need to identify material risks that could affect deal valuation',
      ],
    },
    solution: {
      text: 'The team adopted a structured approach using Lawra AI:',
      steps: [
        { tool: 'Due Diligence', description: 'Documents were batch-uploaded by category. Lawra AI extracted key findings, flagged compliance gaps, and identified missing documents that should have been in the data room.' },
        { tool: 'Legal Summarisation', description: 'Board minutes, shareholder agreements, and lengthy employment contracts were summarised to enable rapid partner review.' },
        { tool: 'Document Comparison', description: 'Multiple versions of the SPA were compared side-by-side, tracking changes and identifying newly inserted clauses.' },
      ],
    },
    results: [
      { icon: Clock, metric: '3 days', label: 'Total Review Time', description: 'Delivered comprehensive DD report 2 days ahead of deadline' },
      { icon: TrendingUp, metric: '12', label: 'Material Issues Found', description: 'Including undisclosed litigation and incomplete IP assignments' },
      { icon: Users, metric: '\u00a3400K', label: 'Price Adjustment', description: 'Findings led to a purchase price reduction based on identified risks' },
    ],
    quote: {
      text: 'The DD tool identified an undisclosed employment tribunal claim buried in a subfolder. That single finding justified the entire investment in Lawra AI.',
      author: 'Partner',
      firm: 'Corporate Law Firm, Manchester',
    },
    relatedTool: '/tools/due-diligence',
    relatedToolName: 'Try Due Diligence',
  },

  'litigation-prep-personal-injury': {
    category: 'Litigation Support',
    title: 'Streamlining Deposition Prep for a Complex Personal Injury Case',
    metaDescription: 'Case study: A personal injury practice reduced deposition preparation time by 65% using Lawra AI, identifying 23 key witness inconsistencies across a multi-party claim.',
    icon: Gavel,
    accentColor: '#FF6B35',
    heroStat: { metric: '65%', label: 'Preparation Time Reduced' },
    overview: 'A personal injury practice handling a complex multi-party claim used Lawra AI\'s Deposition Prep tool to generate comprehensive question frameworks, analyse witness statements, and prepare cross-examination strategies. What typically took two weeks of preparation was completed in under five days.',
    challenge: {
      text: 'The case involved a serious workplace injury with multiple defendants and complex liability issues:',
      points: [
        'Four separate defendants with conflicting accounts of the incident',
        'Over 30 witness statements to analyse for inconsistencies',
        'Expert medical evidence spanning orthopaedic and neurological specialisms',
        'Need to prepare deposition questions tailored to each defendant\'s version of events',
      ],
    },
    solution: {
      text: 'The litigation team used Lawra AI to build their case preparation:',
      steps: [
        { tool: 'Deposition Prep', description: 'Witness statements were uploaded and Lawra AI generated targeted deposition questions, identifying key inconsistencies between accounts and suggesting lines of cross-examination.' },
        { tool: 'Legal Summarisation', description: 'Complex medical reports were summarised into clear, structured overviews that could be quickly referenced during depositions.' },
        { tool: 'Ask Lawra', description: 'The team consulted the AI assistant on procedural points and relevant case law precedents for personal injury liability.' },
      ],
    },
    results: [
      { icon: Clock, metric: '65%', label: 'Time Saved', description: 'Preparation completed in 5 days instead of the usual 14' },
      { icon: TrendingUp, metric: '23', label: 'Key Inconsistencies', description: 'Contradictions identified across witness statements that strengthened the claim' },
      { icon: Users, metric: 'Settled', label: 'Case Outcome', description: 'Strong preparation contributed to a favourable pre-trial settlement' },
    ],
    quote: {
      text: 'The deposition questions Lawra generated were genuinely excellent. It identified contradictions between witness statements that we might have spent days finding manually.',
      author: 'Litigation Solicitor',
      firm: 'Personal Injury Practice, Birmingham',
    },
    relatedTool: '/tools/deposition-prep',
    relatedToolName: 'Try Deposition Prep',
  },

  'legal-research-criminal-defence': {
    category: 'Legal Research',
    title: 'Accelerating Case Law Research for a Criminal Defence Team',
    metaDescription: 'Case study: A criminal defence solicitor researched fraud trial precedents 4x faster with Lawra AI, finding 15+ key authorities and reducing external research costs by 60%.',
    icon: Scale,
    accentColor: '#8B5CF6',
    heroStat: { metric: '4x', label: 'Faster Research Turnaround' },
    overview: 'A criminal defence solicitor preparing for a complex fraud trial used Lawra AI to rapidly research relevant precedents, analyse sentencing guidelines, and prepare legal arguments. Research that would normally take a full week was completed in under two days.',
    challenge: {
      text: 'The defence team was preparing for a multi-count fraud trial with limited time before the PCMH:',
      points: [
        'Complex intersection of fraud, money laundering, and proceeds of crime legislation',
        'Need to identify relevant Court of Appeal authorities on sentencing for similar offences',
        'Client instructions required analysis of abuse of process arguments',
        'Limited budget for external counsel research support',
      ],
    },
    solution: {
      text: 'The solicitor used Lawra AI to accelerate their research workflow:',
      steps: [
        { tool: 'Legal Research', description: 'Lawra AI analysed the specific charges and identified relevant case law precedents, statutory provisions, and sentencing guidelines applicable to the offences.' },
        { tool: 'Legal Summarisation', description: 'Key judgments were summarised with relevant ratio decidendi extracted, enabling quick identification of the most persuasive authorities.' },
        { tool: 'Document Generation', description: 'Defence skeleton arguments were drafted based on the research findings, providing a structured starting point for counsel.' },
      ],
    },
    results: [
      { icon: Clock, metric: '4x', label: 'Faster Research', description: 'Two days of research replaced a full week of manual work' },
      { icon: TrendingUp, metric: '15+', label: 'Key Authorities', description: 'Relevant precedents identified including several the team had not previously considered' },
      { icon: Users, metric: '60%', label: 'Cost Reduction', description: 'Reduced need for external research support and counsel preparation time' },
    ],
    quote: {
      text: 'Lawra found a Court of Appeal authority from 2019 that was directly on point for our abuse of process argument. It completely changed our approach to the case.',
      author: 'Defence Solicitor',
      firm: 'Criminal Defence Practice, Leeds',
    },
    relatedTool: '/tools/legal-research',
    relatedToolName: 'Try Legal Research',
  },

  'document-generation-family-law': {
    category: 'Document Generation',
    title: 'Automating Standard Documents for a Family Law Practice',
    metaDescription: 'Case study: A family law firm cut document drafting time by 90% with Lawra AI, handling 35% more active cases while maintaining consistent quality across the team.',
    icon: MessageSquare,
    accentColor: '#10B981',
    heroStat: { metric: '90%', label: 'Drafting Time Eliminated' },
    overview: 'A busy family law practice serving over 200 active clients used Lawra AI to automate the generation of standard legal documents, correspondence, and consent orders. The practice reported a 90% reduction in time spent on routine drafting, allowing solicitors to take on more client work.',
    challenge: {
      text: 'The practice was struggling with the volume of routine document production:',
      points: [
        'High volume of standard correspondence, consent orders, and court forms',
        'Solicitors spending 30-40% of billable time on routine drafting',
        'Inconsistent document quality when using manual templates',
        'Growing client base requiring faster turnaround on all documentation',
      ],
    },
    solution: {
      text: 'The firm integrated Lawra AI into their daily document production workflow:',
      steps: [
        { tool: 'Document Generation', description: 'Standard documents including client care letters, consent orders, and Form E financial statements were generated using Lawra AI with case-specific details automatically populated.' },
        { tool: 'Email Response Assistant', description: 'Routine client correspondence and opposing solicitor communications were drafted by AI, with solicitors reviewing and personalising before sending.' },
        { tool: 'Legal Summarisation', description: 'Financial disclosure documents were summarised to help solicitors quickly identify key assets and liabilities during settlement negotiations.' },
      ],
    },
    results: [
      { icon: Clock, metric: '90%', label: 'Drafting Time Cut', description: 'Documents that took 45 minutes now take under 5 minutes to generate' },
      { icon: TrendingUp, metric: '35%', label: 'More Client Capacity', description: 'Solicitors handle 35% more active cases with the same team size' },
      { icon: Users, metric: 'Consistent', label: 'Document Quality', description: 'Standardised output eliminated quality variations across the team' },
    ],
    quote: {
      text: 'Our solicitors used to dread the paperwork. Now they generate a perfectly formatted consent order in minutes and spend their time actually advising clients.',
      author: 'Managing Partner',
      firm: 'Family Law Practice, Bristol',
    },
    relatedTool: '/tools/document-generation',
    relatedToolName: 'Try Document Generation',
  },

  'debt-recovery-automation': {
    category: 'Debt Recovery',
    title: 'Scaling Debt Recovery Operations with AI-Powered Letters',
    metaDescription: 'Case study: A debt recovery firm tripled monthly letter output using Lawra AI while improving recovery rates by 22% and maintaining full Pre-Action Protocol compliance.',
    icon: Shield,
    accentColor: '#F59E0B',
    heroStat: { metric: '3x', label: 'Recovery Volume Increase' },
    overview: 'A specialist debt recovery firm used Lawra AI to generate compliant, personalised debt recovery letters at scale. The firm tripled their letter output while maintaining full regulatory compliance, leading to a significant increase in successful recoveries.',
    challenge: {
      text: 'The firm was at capacity with manual letter production and needed to scale:',
      points: [
        'Manual letter drafting was the bottleneck limiting case volume',
        'Each letter required personalisation based on debt type, amount, and debtor circumstances',
        'Strict compliance requirements under Pre-Action Protocol and FCA guidelines',
        'Client demand for faster turnaround on initial demand letters',
      ],
    },
    solution: {
      text: 'Lawra AI was deployed to automate the letter generation pipeline:',
      steps: [
        { tool: 'Debt Recovery Tools', description: 'The firm used Lawra AI\'s debt recovery module to generate Letter Before Action, follow-up demands, and settlement proposals, each tailored to the specific debt and debtor profile.' },
        { tool: 'Document Generation', description: 'Supporting court documents including claim forms and witness statements were generated when cases progressed to litigation.' },
        { tool: 'Email Response Assistant', description: 'Debtor correspondence and payment arrangement communications were drafted efficiently, maintaining a professional and compliant tone.' },
      ],
    },
    results: [
      { icon: Clock, metric: '3x', label: 'Volume Increase', description: 'Monthly letter output tripled without additional staff' },
      { icon: TrendingUp, metric: '22%', label: 'Higher Recovery Rate', description: 'Personalised, timely letters improved debtor response rates' },
      { icon: Users, metric: '100%', label: 'Compliance Maintained', description: 'All letters generated within Pre-Action Protocol and FCA guidelines' },
    ],
    quote: {
      text: 'We went from producing 500 letters a month to over 1,500 without hiring a single additional person. The ROI was immediate and substantial.',
      author: 'Operations Director',
      firm: 'Debt Recovery Firm, Glasgow',
    },
    relatedTool: '/tools/debt-recovery',
    relatedToolName: 'Try Debt Recovery',
  },

  'email-response-employment-law': {
    category: 'Employment Law',
    title: 'How an Employment Law Firm Halved Response Times on Tribunal Correspondence',
    metaDescription: 'Case study: An employment law practice used Lawra AI\'s Email Response Assistant to draft tribunal correspondence 52% faster, improving client satisfaction and case throughput.',
    icon: Mail,
    accentColor: '#EC4899',
    heroStat: { metric: '52%', label: 'Faster Response Turnaround' },
    overview: 'A specialist employment law firm handling over 150 active tribunal cases was struggling to keep up with the volume of correspondence required by employment judges, opposing counsel, and clients. Using Lawra AI\'s Email Response Assistant, the firm cut average response drafting time by 52%, enabling solicitors to manage larger caseloads without sacrificing quality or missing deadlines.',
    challenge: {
      text: 'The firm faced mounting pressure from increasing tribunal caseloads and strict procedural timelines:',
      points: [
        'Employment tribunals require precise, well-drafted responses within tight deadlines',
        'Each case generates substantial correspondence with ACAS, opposing solicitors, and the tribunal itself',
        'Solicitors were spending 2-3 hours per day on routine correspondence rather than case strategy',
        'Inconsistent tone and formatting across team members raised quality concerns',
      ],
    },
    solution: {
      text: 'The firm deployed Lawra AI\'s Email Response Assistant as the first step in their correspondence workflow:',
      steps: [
        { tool: 'Email Response Assistant', description: 'Incoming correspondence was fed into Lawra AI which generated contextually appropriate draft responses, matching the tone and formality required for tribunal communications, ACAS early conciliation letters, and client updates.' },
        { tool: 'Document Generation', description: 'Standard tribunal documents including ET3 response forms, witness statements, and schedule of loss calculations were generated with case-specific details automatically populated.' },
        { tool: 'Legal Summarisation', description: 'Lengthy grievance documents and disciplinary hearing transcripts were summarised to enable quick partner review and strategic decision-making.' },
      ],
    },
    results: [
      { icon: Clock, metric: '52%', label: 'Faster Responses', description: 'Average response drafting time reduced from 45 minutes to under 22 minutes' },
      { icon: TrendingUp, metric: '30%', label: 'More Cases Handled', description: 'Each solicitor now manages 30% more active cases with the same working hours' },
      { icon: Users, metric: 'Zero', label: 'Missed Deadlines', description: 'No tribunal deadlines missed since implementing Lawra AI' },
    ],
    quote: {
      text: 'The Email Response Assistant understands the tone employment tribunals expect. Our drafts now go out faster and with fewer revisions needed from senior partners.',
      author: 'Senior Employment Solicitor',
      firm: 'Employment Law Practice, Edinburgh',
    },
    relatedTool: '/tools/email-response',
    relatedToolName: 'Try Email Response Assistant',
  },

  'document-comparison-property-law': {
    category: 'Property Law',
    title: 'Tracking Lease Variations Across a 300-Unit Residential Portfolio',
    metaDescription: 'Case study: A property law team compared 300+ lease variations using Lawra AI\'s Document Comparison tool, identifying 28 critical deviations that averted significant financial exposure.',
    icon: GitCompare,
    accentColor: '#06B6D4',
    heroStat: { metric: '300+', label: 'Lease Variations Compared' },
    overview: 'A property litigation team acting for a major residential freeholder needed to review and compare over 300 lease variations accumulated over two decades. Using Lawra AI\'s Document Comparison tool, they identified 28 critical deviations from the standard lease terms that had been creating management and service charge disputes across the portfolio.',
    challenge: {
      text: 'The freeholder was facing multiple leaseholder disputes and needed a comprehensive audit of all lease variations:',
      points: [
        'Over 300 lease variation deeds executed across 20 years with different solicitors',
        'No centralised record of which leases had been varied and how',
        'Ongoing disputes about service charge liability, permitted use, and alteration rights',
        'The portfolio was being prepared for sale and required a clean lease position',
      ],
    },
    solution: {
      text: 'The team used Lawra AI to systematically compare every variation against the original standard lease:',
      steps: [
        { tool: 'Document Comparison', description: 'Each lease variation was compared against the original standard lease template, generating precise deviation reports highlighting every changed clause, inserted provision, and deleted obligation.' },
        { tool: 'Legal Summarisation', description: 'Complex variation deeds with multiple schedules were summarised into concise clause-by-clause change logs that could be quickly reviewed by the partner leading the matter.' },
        { tool: 'Contract Review', description: 'The original standard lease and key variations were analysed for risk clauses, identifying provisions that could create liability for the freeholder during the portfolio sale process.' },
      ],
    },
    results: [
      { icon: Clock, metric: '70%', label: 'Time Saved', description: 'Portfolio-wide comparison completed in 2 weeks instead of the estimated 7 weeks' },
      { icon: TrendingUp, metric: '28', label: 'Critical Deviations', description: 'Material deviations found including unauthorised alterations to service charge caps and forfeiture clauses' },
      { icon: Users, metric: 'Resolved', label: 'Pre-Sale Issues', description: 'All identified issues resolved before portfolio marketing, enabling a clean sale' },
    ],
    quote: {
      text: 'Manually comparing 300 lease variations would have been a nightmare. Lawra AI flagged deviations we genuinely did not know existed, including one that had been causing disputes for years.',
      author: 'Property Litigation Partner',
      firm: 'Property Law Firm, London',
    },
    relatedTool: '/tools/document-comparison',
    relatedToolName: 'Try Document Comparison',
  },

  'legal-summarisation-insurance-law': {
    category: 'Insurance Law',
    title: 'Summarising Complex Policy Wordings for a Lloyd\'s Syndicate Dispute',
    metaDescription: 'Case study: An insurance litigation team used Lawra AI to summarise 400+ pages of Lloyd\'s policy wordings, reducing review time by 85% and building a stronger coverage argument.',
    icon: BookOpen,
    accentColor: '#7C3AED',
    heroStat: { metric: '85%', label: 'Review Time Reduced' },
    overview: 'An insurance litigation team preparing for a multi-million pound coverage dispute needed to analyse over 400 pages of policy wordings, endorsements, and slip documentation from a Lloyd\'s syndicate. Lawra AI\'s Legal Summarisation tool distilled the critical coverage terms, exclusions, and conditions precedent into structured summaries that enabled counsel to build a stronger argument in a fraction of the usual time.',
    challenge: {
      text: 'The dispute involved complex layered insurance coverage with extensive documentation:',
      points: [
        'Primary policy, excess layers, and reinsurance treaties totalling 400+ pages',
        'Multiple endorsements modifying coverage terms across different policy years',
        'Need to identify all relevant exclusions, conditions precedent, and notification requirements',
        'Counsel required a comprehensive coverage matrix before the case management conference',
      ],
    },
    solution: {
      text: 'The team used Lawra AI to process the entire policy tower systematically:',
      steps: [
        { tool: 'Legal Summarisation', description: 'Each policy wording and endorsement was summarised with focus on insuring clauses, exclusions, conditions precedent, and aggregation provisions. Lawra AI produced structured summaries identifying coverage triggers and potential gaps.' },
        { tool: 'Document Comparison', description: 'Policy wordings across different years were compared to identify how coverage terms had evolved, revealing a critical change to the notification clause in the third policy year.' },
        { tool: 'Ask Lawra', description: 'The team used the AI chatbot to query specific coverage questions across the summarised policy documents, getting instant answers on whether particular scenarios fell within or outside coverage.' },
      ],
    },
    results: [
      { icon: Clock, metric: '85%', label: 'Time Saved', description: 'Full policy tower review completed in 3 days instead of the estimated 3 weeks' },
      { icon: TrendingUp, metric: 'Critical', label: 'Finding Identified', description: 'Discovered a notification clause change that fundamentally altered the insurer\'s position' },
      { icon: Users, metric: 'Settled', label: 'Dispute Resolution', description: 'Strong policy analysis contributed to a favourable early settlement for the insured' },
    ],
    quote: {
      text: 'Insurance policy wordings are notoriously dense. Lawra AI didn\'t just summarise them\u2014it structured the output so we could immediately see coverage gaps and exclusion interactions.',
      author: 'Insurance Litigation Associate',
      firm: 'Insurance Law Practice, City of London',
    },
    relatedTool: '/tools/legal-summarisation',
    relatedToolName: 'Try Legal Summarisation',
  },

  'ai-chatbot-immigration-law': {
    category: 'Immigration Law',
    title: 'Accelerating Visa Application Research for a Multi-National Practice',
    metaDescription: 'Case study: An immigration law firm used Ask Lawra to research visa categories and policy changes 40% faster, improving initial consultation efficiency and client onboarding.',
    icon: Bot,
    accentColor: '#14B8A6',
    heroStat: { metric: '40%', label: 'Consultation Time Saved' },
    overview: 'A busy immigration law practice handling applications across multiple visa categories was spending excessive time on initial consultations, researching eligibility requirements, and tracking frequent policy changes. Using Ask Lawra, the firm\'s AI chatbot assistant, immigration advisers could quickly research visa categories, compare eligibility criteria, and prepare for client meetings in significantly less time.',
    challenge: {
      text: 'The practice faced unique challenges in a rapidly changing regulatory environment:',
      points: [
        'Immigration rules change frequently, requiring constant monitoring of Home Office guidance',
        'Initial consultations required extensive pre-research to assess visa eligibility across multiple categories',
        'Each client\'s circumstances required analysis against different points-based system requirements',
        'Advisers were spending 60-90 minutes preparing for each initial consultation',
      ],
    },
    solution: {
      text: 'The firm integrated Ask Lawra into their pre-consultation and research workflow:',
      steps: [
        { tool: 'Ask Lawra', description: 'Immigration advisers used the AI chatbot to quickly query visa eligibility requirements, compare Skilled Worker, Global Talent, and Innovator Founder routes, and check maintenance fund requirements for specific client scenarios.' },
        { tool: 'Legal Summarisation', description: 'Lengthy Home Office policy guidance documents and country-specific information were summarised into digestible briefings that advisers could review before consultations.' },
        { tool: 'Document Generation', description: 'Standard client care letters, representation agreements, and checklist documents for different visa categories were generated with case-specific details pre-populated.' },
      ],
    },
    results: [
      { icon: Clock, metric: '40%', label: 'Prep Time Saved', description: 'Pre-consultation research reduced from 75 minutes to under 45 minutes on average' },
      { icon: TrendingUp, metric: '25%', label: 'More Consultations', description: 'Advisers now conduct 25% more initial consultations per week' },
      { icon: Users, metric: '95%', label: 'Client Satisfaction', description: 'Clients report higher confidence in advisers\' thorough knowledge during first meetings' },
    ],
    quote: {
      text: 'Immigration rules are a moving target. Ask Lawra helps us quickly research the current requirements so we can give accurate, confident advice from the very first consultation.',
      author: 'Senior Immigration Adviser',
      firm: 'Immigration Law Firm, London',
    },
    relatedTool: '/tools/ask-lawra',
    relatedToolName: 'Try Ask Lawra',
  },

  'contract-review-technology-ip': {
    category: 'Technology & IP',
    title: 'Reviewing SaaS Agreements and IP Licences for a Tech Startup Portfolio',
    metaDescription: 'Case study: A technology law firm reviewed 40+ startup SaaS agreements and IP licences using Lawra AI, identifying critical IP ownership gaps and data protection compliance issues.',
    icon: Laptop,
    accentColor: '#3B82F6',
    heroStat: { metric: '40+', label: 'Companies Reviewed' },
    overview: 'A technology-focused law firm advising a venture capital fund needed to review the contractual positions of 40+ portfolio companies. Each company had a complex web of SaaS subscription agreements, IP licence arrangements, and data processing addenda. Lawra AI\'s Contract Review tool enabled the team to systematically identify IP ownership risks, problematic SaaS terms, and GDPR compliance gaps across the entire portfolio.',
    challenge: {
      text: 'The VC fund required a comprehensive legal audit before its next funding round:',
      points: [
        'Over 40 portfolio companies, each with 10-30 active technology contracts',
        'Critical need to verify IP ownership chains and assignment completeness',
        'SaaS agreements with varying terms on data portability, lock-in, and liability caps',
        'GDPR compliance audit required across all data processing agreements',
      ],
    },
    solution: {
      text: 'The team used Lawra AI to process the portfolio systematically company by company:',
      steps: [
        { tool: 'Contract Review', description: 'IP licence agreements and technology contracts were analysed for ownership clauses, assignment provisions, and restrictions. Lawra AI flagged 15 companies with incomplete IP assignment chains from founders or contractors.' },
        { tool: 'Document Comparison', description: 'Data processing addenda were compared against the fund\'s standard GDPR requirements, identifying which companies had substandard data protection provisions.' },
        { tool: 'Legal Summarisation', description: 'Complex multi-schedule SaaS agreements were summarised to extract key commercial terms\u2014pricing, renewal mechanisms, data portability rights, and liability caps\u2014into standardised comparison tables.' },
      ],
    },
    results: [
      { icon: Clock, metric: '75%', label: 'Time Saved', description: 'Full portfolio review completed in 3 weeks instead of the estimated 12 weeks' },
      { icon: TrendingUp, metric: '15', label: 'IP Issues Found', description: 'Companies identified with incomplete IP ownership chains requiring immediate remediation' },
      { icon: Users, metric: '8', label: 'GDPR Gaps Fixed', description: 'Data processing agreements updated to meet compliance standards before the funding round' },
    ],
    quote: {
      text: 'Reviewing 40 companies\' technology contracts manually would have taken three months. Lawra AI let us deliver a comprehensive portfolio audit in three weeks, and we caught IP issues that could have derailed the funding round.',
      author: 'Technology Partner',
      firm: 'Technology Law Firm, London',
    },
    relatedTool: '/tools/contract-review',
    relatedToolName: 'Try Contract Review',
  },

  'regulatory-compliance-financial-services': {
    category: 'Financial Services',
    title: 'Ensuring FCA Compliance Across Client Documentation for a Wealth Manager',
    metaDescription: 'Case study: A financial services law team used Lawra AI to audit client-facing documents for FCA compliance, achieving 100% regulatory alignment across 500+ documents.',
    icon: Building,
    accentColor: '#EF4444',
    heroStat: { metric: '100%', label: 'Compliance Achieved' },
    overview: 'A law firm acting for a regulated wealth management company needed to audit over 500 client-facing documents for FCA compliance ahead of a regulatory inspection. The documents included investment agreements, risk warnings, suitability reports, and client correspondence. Lawra AI enabled the team to systematically review every document for regulatory alignment, identify gaps, and generate compliant replacement drafts in record time.',
    challenge: {
      text: 'The wealth manager faced an imminent FCA thematic review with significant documentation gaps:',
      points: [
        'Over 500 client-facing documents accumulated across 8 years of operations',
        'FCA Consumer Duty requirements had changed the compliance bar significantly',
        'Suitability reports needed to demonstrate proper client outcome considerations',
        'Investment risk warnings required updating to reflect current regulatory guidance',
      ],
    },
    solution: {
      text: 'The legal team deployed Lawra AI across the entire document estate:',
      steps: [
        { tool: 'Contract Review', description: 'Investment management agreements were reviewed for FCA-required clauses including fee disclosure, conflict of interest provisions, complaint handling procedures, and cancellation rights.' },
        { tool: 'Document Generation', description: 'Non-compliant documents were replaced with FCA-aligned drafts generated by Lawra AI, incorporating current Consumer Duty language, appropriate risk warnings, and clear fee structures.' },
        { tool: 'Document Comparison', description: 'Updated documents were compared against FCA model templates and the firm\'s own compliance standards to verify full alignment before the regulatory review.' },
        { tool: 'Legal Summarisation', description: 'FCA guidance papers and policy statements were summarised to create a compliance checklist that the review team used to verify each document.' },
      ],
    },
    results: [
      { icon: Clock, metric: '4 weeks', label: 'Total Audit Time', description: 'Complete document estate reviewed and remediated 6 weeks ahead of the FCA inspection' },
      { icon: TrendingUp, metric: '500+', label: 'Documents Reviewed', description: 'Every client-facing document audited for FCA compliance' },
      { icon: Users, metric: 'Clean', label: 'FCA Outcome', description: 'Regulatory inspection completed with no material findings or enforcement action' },
    ],
    quote: {
      text: 'The FCA inspection could have been catastrophic without this preparation. Lawra AI allowed us to audit and remediate 500+ documents in a timeframe that would have been physically impossible with manual review.',
      author: 'Regulatory Partner',
      firm: 'Financial Services Law Firm, Canary Wharf',
    },
    relatedTool: '/tools/contract-review',
    relatedToolName: 'Try Contract Review',
  },

  'inline-review-lease-negotiation': {
    category: 'Inline Review',
    title: 'Annotating and Redlining a 90-Page Commercial Lease in Under 2 Hours',
    metaDescription: 'Case study: A property solicitor used Lawra AI\'s Inline Review tool to annotate a 90-page commercial lease in under 2 hours, producing a fully marked-up document ready for negotiation.',
    icon: FileText,
    accentColor: '#F97316',
    heroStat: { metric: '2hrs', label: 'Full Review and Markup Time' },
    overview: 'A property solicitor acting for a tenant in a complex lease negotiation needed to thoroughly review and annotate a 90-page commercial lease. Using Lawra AI\'s Inline Review & Editor tool, the solicitor produced a fully marked-up document with risk commentary, suggested amendments, and negotiation notes — all within two hours.',
    challenge: {
      text: 'The landlord\'s solicitors had produced a heavily amended lease with non-standard terms that required careful review:',
      points: [
        'A 90-page lease with complex service charge provisions and break clause conditions',
        'Non-standard alienation provisions restricting the tenant\'s ability to assign or sublet',
        'Hidden obligations buried in schedules that increased the tenant\'s total occupancy cost',
        'A 48-hour turnaround requested by the client to maintain their negotiating position',
      ],
    },
    solution: {
      text: 'The solicitor used the Inline Review tool to work through the document systematically:',
      steps: [
        { tool: 'Inline Review & Editor', description: 'Uploaded the full lease and received AI-generated annotations highlighting risk clauses, unusual provisions, and areas requiring negotiation. Each annotation included a severity rating and suggested alternative wording.' },
        { tool: 'Contract Review', description: 'Ran a parallel risk assessment to ensure nothing was missed, cross-referencing the inline annotations against a comprehensive risk framework.' },
        { tool: 'Ask Lawra', description: 'Queried specific provisions — such as the interaction between the break clause and the service charge cap — to confirm the legal position before drafting amendments.' },
      ],
    },
    results: [
      { icon: Clock, metric: '2hrs', label: 'Total Review Time', description: 'Complete annotated lease produced in under 2 hours vs. a typical full-day review' },
      { icon: TrendingUp, metric: '34', label: 'Issues Flagged', description: 'Risk clauses, unusual terms, and hidden obligations identified with suggested amendments' },
      { icon: Users, metric: '£45K', label: 'Client Savings', description: 'Amendments negotiated from the annotations saved the tenant significant occupancy costs' },
    ],
    quote: {
      text: 'The Inline Review tool gave me a head start that would normally take half a day. I was able to focus on the complex negotiation points rather than line-by-line reading.',
      author: 'Property Solicitor',
      firm: 'Regional Law Firm, Manchester',
    },
    relatedTool: '/tools/inline-review',
    relatedToolName: 'Try Inline Review',
  },

  'ask-lawra-client-consultations': {
    category: 'Ask Lawra AI',
    title: 'Cutting Initial Client Consultation Research Time by 60% with AI Chat',
    metaDescription: 'Case study: A high-street solicitor used Ask Lawra AI to instantly research procedural questions during client meetings, cutting post-meeting research time by 60%.',
    icon: Bot,
    accentColor: '#0EA5E9',
    heroStat: { metric: '60%', label: 'Research Time Reduction' },
    overview: 'A high-street solicitor handling a mixed caseload of family, housing, and employment matters was spending 2-3 hours after every client consultation researching procedural questions that arose during the meeting. Using Ask Lawra AI, the solicitor began resolving these questions in real time during consultations, dramatically reducing follow-up research.',
    challenge: {
      text: 'Running a busy general practice meant dealing with diverse legal questions across multiple practice areas:',
      points: [
        'Clients expected immediate answers to procedural and statutory questions during meetings',
        'Post-meeting research consumed 2-3 hours per consultation, delaying client updates',
        'Limitation periods, court fees, and procedural requirements varied by case type and needed quick verification',
        'The solicitor couldn\'t justify the cost of specialist counsel for routine procedural queries',
      ],
    },
    solution: {
      text: 'Ask Lawra AI became a constant companion during client meetings:',
      steps: [
        { tool: 'Ask Lawra', description: 'During consultations, the solicitor queried limitation periods, court fee schedules, and procedural requirements in real time. Lawra provided clear, referenced answers within seconds.' },
        { tool: 'Legal Research', description: 'For more complex questions arising from consultations, the solicitor used Legal Research to quickly pull relevant case law and statutory provisions, building a research note in minutes rather than hours.' },
        { tool: 'Email Response Assistant', description: 'Post-meeting, the solicitor used the Email Response tool to draft comprehensive client advice letters incorporating the research gathered during the consultation.' },
      ],
    },
    results: [
      { icon: Clock, metric: '60%', label: 'Research Time Cut', description: 'Post-meeting research reduced from 2-3 hours to under 45 minutes per consultation' },
      { icon: TrendingUp, metric: '3x', label: 'More Consultations', description: 'Freed-up time allowed the solicitor to take on significantly more client meetings per week' },
      { icon: Users, metric: '95%', label: 'Client Satisfaction', description: 'Clients reported higher satisfaction from receiving immediate, informed guidance during meetings' },
    ],
    quote: {
      text: 'Ask Lawra changed how I run consultations. Clients get answers there and then, and I\'m not staying late every evening catching up on research.',
      author: 'Sole Practitioner',
      firm: 'High Street Practice, Birmingham',
    },
    relatedTool: '/tools/ask-lawra',
    relatedToolName: 'Try Ask Lawra',
  },

  'notetaker-client-instructions': {
    category: 'Notetaker',
    title: 'Capturing and Structuring Client Instructions from 50+ Weekly Calls',
    metaDescription: 'Case study: A conveyancing practice used Lawra AI\'s Notetaker to capture and structure client instructions from 50+ weekly calls, eliminating 10 hours of manual note-taking per week.',
    icon: MessageSquare,
    accentColor: '#A855F7',
    heroStat: { metric: '50+', label: 'Calls Captured Per Week' },
    overview: 'A busy conveyancing practice handling 150+ active matters was losing significant fee-earning time to manual attendance note creation. Solicitors were spending up to 20 minutes after each client call writing up notes. Using Lawra AI\'s Notetaker, the practice automated this entirely — recording calls and producing clean, structured attendance notes ready for the file.',
    challenge: {
      text: 'The practice\'s volume of client communications was creating a significant administrative burden:',
      points: [
        'Each solicitor was handling 30-40 active matters with regular client calls',
        'Manual attendance notes took 15-20 minutes per call to write up',
        'Notes were often incomplete or delayed when solicitors were under time pressure',
        'Compliance reviews flagged inconsistent note-taking standards across the team',
      ],
    },
    solution: {
      text: 'The Notetaker was rolled out across the conveyancing team:',
      steps: [
        { tool: 'Notetaker', description: 'Client calls were recorded (with consent) and Lawra AI produced structured attendance notes capturing key instructions, agreed actions, and follow-up items. Notes were formatted consistently across all matters.' },
        { tool: 'Instruction & Action', description: 'Client instructions captured by the Notetaker were fed into the Instruction & Action tool, which generated task lists and next steps for each matter — ensuring nothing fell through the cracks.' },
        { tool: 'Document Generation', description: 'Standardised letters arising from client instructions (such as requests for searches, mortgage redemption statements, or completion notices) were generated directly from the captured instructions.' },
      ],
    },
    results: [
      { icon: Clock, metric: '10hrs', label: 'Weekly Time Saved', description: 'Team collectively saved over 10 hours per week previously spent on manual note-taking' },
      { icon: TrendingUp, metric: '100%', label: 'Compliance', description: 'Every client interaction now has a consistent, complete attendance note on file' },
      { icon: Users, metric: '15%', label: 'More Matters', description: 'Freed capacity allowed the team to take on 15% more active matters' },
    ],
    quote: {
      text: 'The Notetaker paid for itself in the first week. Our attendance notes are now better than anything we produced manually, and they\'re done before the call even ends.',
      author: 'Head of Conveyancing',
      firm: 'Regional Practice, Leeds',
    },
    relatedTool: '/tools/notetaker',
    relatedToolName: 'Try Notetaker',
  },

  'document-generation-corporate-restructure': {
    category: 'Document Generation',
    title: 'Generating 80+ Board Resolutions and Minutes for a Group Restructure',
    metaDescription: 'Case study: A corporate law team used Lawra AI to generate 80+ board resolutions and shareholder minutes for a 15-entity group restructure, completing in 3 days instead of 3 weeks.',
    icon: FileText,
    accentColor: '#22C55E',
    heroStat: { metric: '80+', label: 'Documents Generated' },
    overview: 'A corporate law team advising on a complex group restructure involving 15 entities needed to produce board resolutions, shareholder written resolutions, and minutes for every step of the transaction. Using Lawra AI\'s Document Generation tool, the team produced over 80 tailored corporate documents in just 3 days.',
    challenge: {
      text: 'The restructure involved multiple simultaneous corporate actions across the group:',
      points: [
        'Board resolutions required for each entity at each stage of the restructure',
        'Shareholder written resolutions with varying approval thresholds by entity type',
        'Companies House filings, stock transfer forms, and updated registers for each step',
        'A 2-week deadline imposed by the client\'s tax advisors to achieve year-end completion',
      ],
    },
    solution: {
      text: 'The team used Document Generation as the core workflow engine:',
      steps: [
        { tool: 'Document Generation', description: 'Board resolutions, shareholder resolutions, and minutes were generated for each entity and each transaction step. Each document was tailored to the specific entity\'s articles and the particular corporate action being approved.' },
        { tool: 'Document Comparison', description: 'Generated documents were compared against the firm\'s precedent bank to ensure consistency with house style and best practice.' },
        { tool: 'Legal Summarisation', description: 'A summary schedule was generated tracking which resolutions had been produced, signed, and filed — giving the partner a real-time view of completion status.' },
      ],
    },
    results: [
      { icon: Clock, metric: '3 days', label: 'Completion Time', description: '80+ documents produced in 3 days instead of the estimated 3 weeks of manual drafting' },
      { icon: TrendingUp, metric: '80+', label: 'Documents', description: 'Board resolutions, shareholder resolutions, minutes, and filings for 15 entities' },
      { icon: Users, metric: '85%', label: 'Time Saved', description: 'Team freed to focus on the substantive tax and commercial aspects of the restructure' },
    ],
    quote: {
      text: 'We generated more corporate documents in three days than our team would normally produce in three weeks. The quality was consistent and the tailoring was spot-on.',
      author: 'Corporate Partner',
      firm: 'City Law Firm, London',
    },
    relatedTool: '/tools/document-generation',
    relatedToolName: 'Try Document Generation',
  },

  'contract-review-nhs-procurement': {
    category: 'Contract Review',
    title: 'Reviewing NHS Standard Contract Terms Across 25 Service Agreements',
    metaDescription: 'Case study: A healthcare law team used Lawra AI to review 25 NHS standard contract variations, identifying non-compliant terms and saving 70% of review time before the funding deadline.',
    icon: Shield,
    accentColor: '#DC2626',
    heroStat: { metric: '25', label: 'Contracts Reviewed' },
    overview: 'A healthcare law team advising an NHS Trust needed to review 25 service agreements with commissioned providers before a regulatory funding deadline. Each contract was based on the NHS Standard Contract but contained provider-specific variations that required careful analysis. Using Lawra AI, the team completed the review in 4 days instead of the projected 3 weeks.',
    challenge: {
      text: 'The NHS Trust was approaching a critical deadline for service agreement compliance:',
      points: [
        '25 provider agreements, each with different service specifications and local variations',
        'Contracts needed to be checked against current NHS Standard Contract terms and conditions',
        'Service specifications required validation against CQC registration and NHS England guidance',
        'A 2-week deadline before the funding allocation review with NHS England',
      ],
    },
    solution: {
      text: 'Lawra AI was deployed to systematically review each contract:',
      steps: [
        { tool: 'Contract Review', description: 'Each provider agreement was analysed for deviations from the NHS Standard Contract, flagging non-compliant liability caps, termination provisions, and information governance clauses.' },
        { tool: 'Document Comparison', description: 'Service specifications were compared across providers delivering similar services, identifying inconsistencies in performance standards and reporting requirements.' },
        { tool: 'Document Generation', description: 'Variation letters were drafted for each provider where amendments were required, tailored to the specific non-compliant terms identified in their agreement.' },
      ],
    },
    results: [
      { icon: Clock, metric: '70%', label: 'Time Saved', description: 'Review completed in 4 days instead of the projected 3 weeks' },
      { icon: TrendingUp, metric: '18', label: 'Non-Compliant Terms', description: 'Critical deviations from NHS Standard Contract identified across the portfolio' },
      { icon: Users, metric: '25', label: 'Variation Letters', description: 'Provider-specific amendment letters generated and sent before the funding deadline' },
    ],
    quote: {
      text: 'We met a deadline that honestly felt impossible when we first scoped the work. Lawra AI made it manageable and thorough.',
      author: 'Healthcare Law Partner',
      firm: 'Specialist Health Practice, Bristol',
    },
    relatedTool: '/tools/contract-review',
    relatedToolName: 'Try Contract Review',
  },
};

const allSlugs = Object.keys(caseStudyData);

const CaseStudy = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const cs = caseStudyData[slug];

  const currentIndex = cs ? allSlugs.indexOf(slug) : -1;
  const prevSlug = currentIndex > 0 ? allSlugs[currentIndex - 1] : null;
  const nextSlug = currentIndex < allSlugs.length - 1 ? allSlugs[currentIndex + 1] : null;
  const Icon = cs ? cs.icon : FileText;

  usePageSEO({
    title: cs ? `${cs.title} - ${cs.category} Case Study` : 'Case Study Not Found',
    description: cs ? cs.metaDescription : 'The requested case study could not be found.',
    path: `/use-cases/${slug}`,
    ogType: 'article',
    structuredData: cs ? {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": cs.title,
      "description": cs.metaDescription,
      "url": `https://lawra-ai.com/use-cases/${slug}`,
      "publisher": {
        "@type": "Organization",
        "name": "Lawra AI",
        "url": "https://lawra-ai.com",
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://lawra-ai.com/use-cases/${slug}`,
      },
      "about": {
        "@type": "SoftwareApplication",
        "name": "Lawra AI",
        "applicationCategory": "BusinessApplication",
      },
    } : null,
  });

  if (!cs) {
    return (
      <div className="landing-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <MatrixBackground />
        <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <h1 className="section-title" style={{ marginBottom: '1rem' }}>Case Study Not Found</h1>
          <Button className="sign-in-btn" onClick={() => navigate('/use-cases')}>Back to Use Cases</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-page">
      <MatrixBackground />
      <MarketingHeader />

      {/* Case Study Hero */}
      <section className="cs-hero" data-testid="case-study-hero" style={{ '--accent': cs.accentColor }}>
        <div className="cs-hero-inner">
          <button className="cs-back-btn" onClick={() => navigate('/use-cases')} data-testid="back-to-use-cases">
            <ArrowLeft size={18} /> All Use Cases
          </button>
          <div className="cs-hero-badge">
            <Icon size={18} />
            <span>{cs.category}</span>
          </div>
          <h1 className="cs-hero-title">{cs.title}</h1>
          <div className="cs-hero-stat">
            <span className="cs-hero-stat-metric">{cs.heroStat.metric}</span>
            <span className="cs-hero-stat-label">{cs.heroStat.label}</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="cs-content" data-testid="case-study-content">
        {/* Overview */}
        <section className="cs-section">
          <h2 className="cs-section-title">Overview</h2>
          <p className="cs-body">{cs.overview}</p>
        </section>

        {/* Challenge */}
        <section className="cs-section">
          <h2 className="cs-section-title">The Challenge</h2>
          <p className="cs-body">{cs.challenge.text}</p>
          <ul className="cs-list">
            {cs.challenge.points.map((point, i) => (
              <li key={i}><CheckCircle size={16} className="cs-list-icon" />{point}</li>
            ))}
          </ul>
        </section>

        {/* Solution */}
        <section className="cs-section">
          <h2 className="cs-section-title">The Solution</h2>
          <p className="cs-body">{cs.solution.text}</p>
          <div className="cs-steps">
            {cs.solution.steps.map((step, i) => (
              <div key={i} className="cs-step" style={{ '--accent': cs.accentColor }}>
                <div className="cs-step-num">{i + 1}</div>
                <div>
                  <h4 className="cs-step-tool">{step.tool}</h4>
                  <p className="cs-step-desc">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Results */}
        <section className="cs-section">
          <h2 className="cs-section-title">Results</h2>
          <div className="cs-results-grid">
            {cs.results.map((r, i) => {
              const RIcon = r.icon;
              return (
                <div key={i} className="cs-result-card" style={{ '--accent': cs.accentColor }}>
                  <RIcon size={24} className="cs-result-icon" />
                  <div className="cs-result-metric">{r.metric}</div>
                  <div className="cs-result-label">{r.label}</div>
                  <p className="cs-result-desc">{r.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Quote */}
        <section className="cs-quote-section" style={{ '--accent': cs.accentColor }}>
          <blockquote className="cs-quote">
            <p>"{cs.quote.text}"</p>
            <footer>
              <strong>{cs.quote.author}</strong>
              <span>{cs.quote.firm}</span>
            </footer>
          </blockquote>
        </section>

        {/* Navigation */}
        <div className="cs-nav">
          {prevSlug ? (
            <Button variant="ghost" className="cs-nav-btn" onClick={() => navigate(`/use-cases/${prevSlug}`)} data-testid="prev-case-study">
              <ArrowLeft size={16} /> Previous
            </Button>
          ) : <div />}
          {nextSlug ? (
            <Button variant="ghost" className="cs-nav-btn" onClick={() => navigate(`/use-cases/${nextSlug}`)} data-testid="next-case-study">
              Next <ArrowRight size={16} />
            </Button>
          ) : <div />}
        </div>
      </article>

      {/* CTA */}
      <section className="cta-section" data-testid="case-study-cta">
        <div className="cta-content">
          <h2>Experience These Results Yourself</h2>
          <p>Start with a free trial and see what Lawra AI can do for your practice</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              data-testid="case-study-try-tool-btn"
              className="try-now-btn"
              size="lg"
              onClick={async () => {
                try {
                  const { auth } = await import('../firebase');
                  if (auth.currentUser) {
                    navigate(cs.relatedTool);
                  } else {
                    navigate('/onboarding');
                  }
                } catch {
                  navigate('/onboarding');
                }
              }}
            >
              {cs.relatedToolName}
            </Button>
          </div>
          <p className="cta-subtext">No credit card required</p>
        </div>
      </section>
    </div>
  );
};

export default CaseStudy;
